import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { convertImageToBase64 } from './convertImageToBase64';
import { uploadPdf } from '../api';
import { t } from '../i18n';

/*
 * PDF Generation Utility with Consistent Width Handling
 * 
 * This utility ensures that all PDF pages (single or multi-page) have the same width
 * by using consistent scaling and container setup:
 * 
 * - Fixed container width: 704px for all pages
 * - Fixed PDF page width: 595pt (A4 standard)
 * - Consistent scaling calculation: 595pt / 704px
 * - Same html2canvas configuration for all pages
 * - Items per page: 10 (configurable)
 * 
 * Page Numbering Logic:
 * - Single-page PDFs: No page numbers shown
 * - Multi-page PDFs: "Страна X од Y" shown on ALL pages
 * 
 * Content Logic:
 * - calculation-1 (additional details): Appears ONLY on the LAST page
 * - note-section (notes): Appears ONLY on the LAST page
 * - footer-2 (business details): Appears on ALL pages
 * - footer-1 (signatures): Appears ONLY on the LAST page
 * - Single-page PDFs: All sections appear (it's both first and last page)
 * 
 * Both single-page and multi-page PDFs will have identical width rendering.
 */

const apiUrl = process.env.REACT_APP_API_URL;

// Configuration constants
const ITEMS_PER_PAGE = 10;

// Helper function to format date from YYYY-MM-DD to DD/MM/YYYY
const formatDateForPrint = (dateString) => {
  if (!dateString) return '';
  const parts = dateString.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  }
  return dateString; // Return original if not in expected format
};

// Helper function to create a clone of the invoice with modified content
const createInvoiceClone = async (divInvoice, invoice, isFirstPage, pageNumber, totalPages, documentType = 'delivery') => {
  const clonedInvoice = divInvoice.cloneNode(true);

  // Handle header section visibility - only show on first page (recipient details, date, etc.)
  const headerSection = clonedInvoice.querySelector('#header');
  if (headerSection) {
    if (isFirstPage) {
      headerSection.style.display = 'flex'; // Show header on first page
    } else {
      headerSection.style.display = 'none'; // Hide header on subsequent pages
    }
  }

  // Update logo (logo section stays on all pages)
  const logoImg = clonedInvoice.querySelector('img[alt="Лого на компанијата"]');
  if (logoImg && invoice.logo) {
    const base64Logo = await convertImageToBase64('../../images/logo_green.png');
    if (base64Logo) {
      logoImg.src = base64Logo;
    }
  }

  // Update invoice number and add page numbers for all pages when there's pagination
  const invoiceNumberInput = clonedInvoice.querySelector('input[name="invoiceNumber"]');
  const invoiceNumberLabel = clonedInvoice.querySelector('label[for="invoiceNumber"]');
  
  if (invoiceNumberInput && invoiceNumberLabel) {
    // Hide the input field for print
    invoiceNumberInput.style.display = 'none';
    
    // Determine the correct document type label
    const documentLabel = documentType === 'invoice' ? 'Фактура' : 'Испратница';
    
    // Update the label to show the invoice number
    if (totalPages > 1) {
      // For multi-page documents, show page numbers on all pages
      invoiceNumberLabel.textContent = `${documentLabel} #${invoice.invoiceNumber}`;
      
      // Add page indicator for all pages when there's pagination
      const invoiceNumberSection = clonedInvoice.querySelector('.invoice-number-section') || 
                                  clonedInvoice.querySelector('div:has(input[name="invoiceNumber"])');
      if (invoiceNumberSection) {
        const pageIndicator = document.createElement('div');
        pageIndicator.className = 'text-sm text-gray-600 text-center';
        pageIndicator.textContent = `Страна ${pageNumber} од ${totalPages}`;
        invoiceNumberSection.parentNode.insertBefore(pageIndicator, invoiceNumberSection);
      }
    } else {
      // For single-page documents, keep original invoice number without page indicator
      invoiceNumberLabel.textContent = `${documentLabel} #${invoice.invoiceNumber}`;
    }
  }

  // Update table rows to show only the current page items
  const tableBody = clonedInvoice.querySelector('tbody');
  if (tableBody) {
    // Clear existing rows
    tableBody.innerHTML = '';
    
    // Add rows for current page
    invoice.items.forEach((item, idx) => {
      const tr = document.createElement('tr');
      tr.className = 'border border-gray-300';
      
      // Calculate global index if this is not the first page
      const globalIdx = isFirstPage ? idx : ((pageNumber - 1) * ITEMS_PER_PAGE) + idx;

      tr.innerHTML = `
        <td class="p-2 text-xs align-middle">${globalIdx + 1}</td>
        <td class="relative">
          <textarea class="w-full p-1 text-xs resize-none leading-tight" rows="2">${item.name || ''}</textarea>
        </td>
        <td>
          <input type="text" value="пар" class="w-full text-xs text-right"/>
        </td>
        <td>
          <input type="number" value="${Math.floor(item.quantity)}" class="w-full text-right"/>
        </td>
        <td class="p-1 print:hidden"></td>
      `;
      
      tableBody.appendChild(tr);
    });
  }

  // Handle footer visibility based on page position
  const isLastPage = pageNumber === totalPages;
  
  // calculation-1 (additional details) should appear ONLY on the LAST page
  const calculation1 = clonedInvoice.querySelector('#calculation-1');
  if (calculation1) {
    if (isLastPage) {
      calculation1.style.display = 'flex'; // Show on last page
    } else {
      calculation1.style.display = 'none'; // Hide on all other pages
    }
  }
  
  // note-section should appear ONLY on the LAST page
  const noteSection = clonedInvoice.querySelector('#note-section');
  if (noteSection) {
    if (isLastPage) {
      noteSection.style.display = 'block'; // Show on last page
    } else {
      noteSection.style.display = 'none'; // Hide on all other pages
    }
  }
  
  // footer-2 (business details) should appear on ALL pages
  const footer2 = clonedInvoice.querySelector('#footer-2');
  if (footer2) {
    footer2.style.display = 'block';
    footer2.style.pageBreakInside = 'avoid';
    if (!isFirstPage) {
      footer2.style.marginTop = 'auto';
    }
  }
  
  // footer-1 (signatures) should appear ONLY on the LAST page
  const footer1 = clonedInvoice.querySelector('#footer-1');
  if (footer1) {
    if (isLastPage) {
      footer1.style.display = 'flex'; // Show on last page
      footer1.style.pageBreakInside = 'avoid';
    } else {
      footer1.style.display = 'none'; // Hide on all other pages
    }
  }

  return clonedInvoice;
};

// Helper function to apply print styles and convert inputs to spans
const prepareForRendering = (clonedInvoice) => {
  const printStyles = document.createElement('style');
  printStyles.textContent = `
    body {
      margin: 0;
      padding: 20px;
    }
    input, textarea, select {
      border: none !important;
      background: transparent !important;
    }
    .print-hidden {
      display: none !important;
    }
    .print-show {
      display: inline !important;
    }
    select {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      background: transparent !important;
      border: none !important;
    }
    body, #invoice {
      display: flex;
      flex-direction: column;
    }
    input[type="date"]::-webkit-calendar-picker-indicator { 
      display: none; 
    }
  `;
  clonedInvoice.appendChild(printStyles);

  const inputs = clonedInvoice.querySelectorAll('input, textarea');
  const originals = [];
  
  inputs.forEach(input => {
    const span = document.createElement('span');
    
    // Handle textareas differently to preserve line breaks
    if (input.tagName.toLowerCase() === 'textarea') {
      span.innerHTML = input.value.replace(/\n/g, '<br>');
      span.style.display = 'block';
      span.style.whiteSpace = 'pre-wrap';
      span.style.wordBreak = 'break-words';
    } else {
      // Format date inputs for printing
      let displayValue = input.value;
      if (input.type === 'date' && input.value) {
        displayValue = formatDateForPrint(input.value);
      }
      
      span.textContent = displayValue;
      span.style.display = 'inline-block';
      span.style.verticalAlign = 'middle';
    }
    
    // Copy styles
    span.className = input.className;
    span.style.width = input.style.width;
    span.style.height = input.style.height;
    span.style.border = 'none';
    span.style.background = 'transparent';
    span.style.boxSizing = 'border-box';

    input.parentNode.replaceChild(span, input);
    originals.push({ span, input });
  });

  return originals;
};

// Shared function to generate canvas with consistent scaling
const generateCanvasFromInvoice = async (clonedInvoice) => {
  const originals = prepareForRendering(clonedInvoice);

  // Create temp container with exactly the same properties every time
  const tempContainer = document.createElement('div');
  tempContainer.id = 'temp-invoice';
  tempContainer.style.position = 'absolute';
  tempContainer.style.left = '-9999px';
  tempContainer.style.top = '20px';
  tempContainer.style.width = '704px'; // Fixed width for consistency
  tempContainer.style.height = 'auto'; // Let height be automatic
  tempContainer.style.backgroundColor = 'white';
  tempContainer.style.padding = '0';
  tempContainer.style.margin = '0';
  tempContainer.style.border = 'none';
  tempContainer.style.boxSizing = 'border-box';
  tempContainer.appendChild(clonedInvoice);
  document.body.appendChild(tempContainer);

  // Wait for fonts and layout to stabilize
  await document.fonts.ready;
  await new Promise(resolve => setTimeout(resolve, 300));

  // Use consistent scaling calculation
  const targetPageWidthPt = 595; // A4 width in points
  const containerWidthPx = tempContainer.offsetWidth;
  const scale = targetPageWidthPt / containerWidthPx;

  const canvas = await html2canvas(clonedInvoice, {
    scale: scale,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    width: containerWidthPx,
    height: tempContainer.offsetHeight,
    scrollX: 0,
    scrollY: 0,
    windowWidth: containerWidthPx,
    windowHeight: tempContainer.offsetHeight
  });

  // Cleanup
  originals.forEach(item => item.span.parentNode.replaceChild(item.input, item.span));
  document.body.removeChild(tempContainer);

  return canvas;
};

// Generate canvas for a paginated page
const generatePageCanvas = async (divInvoice, pageInvoice, isFirstPage, pageNumber, totalPages, documentType = 'delivery') => {
  const clonedInvoice = await createInvoiceClone(divInvoice, pageInvoice, isFirstPage, pageNumber, totalPages, documentType);
  return await generateCanvasFromInvoice(clonedInvoice);
};

// Generate canvas for single page (original logic)
const generateSinglePageCanvas = async (divInvoice, invoice, documentType = 'delivery') => {
  const clonedInvoice = divInvoice.cloneNode(true);

  const logoImg = clonedInvoice.querySelector('img[alt="Лого на компанијата"]');
  if (logoImg && invoice.logo) {
    const base64Logo = await convertImageToBase64('../../images/logo_green.png');
    if (base64Logo) {
      logoImg.src = base64Logo;
    }
  }

  // Handle invoice number display for single page
  const invoiceNumberInput = clonedInvoice.querySelector('input[name="invoiceNumber"]');
  const invoiceNumberLabel = clonedInvoice.querySelector('label[for="invoiceNumber"]');
  
  if (invoiceNumberInput && invoiceNumberLabel) {
    // Hide the input field for print
    invoiceNumberInput.style.display = 'none';
    
    // Determine the correct document type label
    const documentLabel = documentType === 'invoice' ? 'Фактура' : 'Испратница';
    
    // Update the label to show the invoice number
    invoiceNumberLabel.textContent = `${documentLabel} #${invoice.invoiceNumber}`;
  }

  // For single-page PDFs, show all sections (it's both first and last page)
  const calculation1 = clonedInvoice.querySelector('#calculation-1');
  const noteSection = clonedInvoice.querySelector('#note-section');
  const footer1 = clonedInvoice.querySelector('#footer-1');
  const footer2 = clonedInvoice.querySelector('#footer-2');
  
  if (calculation1) {
    calculation1.style.display = 'flex'; // Show calculations on single page
  }
  
  if (noteSection) {
    noteSection.style.display = 'block'; // Show note section on single page
  }
  
  if (footer1) {
    footer1.style.display = 'flex'; // Show signatures on single page
    footer1.style.pageBreakInside = 'avoid';
  }
  
  if (footer2) {
    footer2.style.display = 'block'; // Show business details on single page
    footer2.style.pageBreakInside = 'avoid';
  }

  return await generateCanvasFromInvoice(clonedInvoice);
};

export const handlePrintNote = async (invoice, showAlert, closeAlert, documentType = 'delivery', uploadToServer = true, documentId = null) => {
  const divInvoice = document.getElementById('invoice');
  if (!divInvoice) return;

  if (!invoice.invoiceNumber) {
    showAlert('error', t('Недостасуваат информации'), t('Бројот на документот не е внесен!'));
    return;
  }

  if (invoice.items.length === 0) {
    showAlert('error', t('Недостасуваат ставки'), t('Мора да имате барем една ставка!'));
    return;
  }

  try {
    showAlert('info', t('Генерирање PDF'), t('Ве молиме почекајте додека се генерира PDF документот...'));

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = 595; // A4 pt - consistent width for all pages
    
    // Check if we need pagination (more than 10 items)
    const totalItems = invoice.items.length;
    const needsPagination = totalItems > ITEMS_PER_PAGE;
    
    if (needsPagination) {
      // Handle multi-page PDF
      const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
      
      for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
        const startIdx = pageIndex * ITEMS_PER_PAGE;
        const endIdx = Math.min(startIdx + ITEMS_PER_PAGE, totalItems);
        const pageItems = invoice.items.slice(startIdx, endIdx);
        const isFirstPage = pageIndex === 0;
        
        // Create a page-specific clone
        const pageInvoice = { ...invoice, items: pageItems };
        
        // Generate the page content
        const pageCanvas = await generatePageCanvas(divInvoice, pageInvoice, isFirstPage, pageIndex + 1, totalPages, documentType);
        
        if (pageIndex > 0) {
          pdf.addPage();
        }
        
        const imgData = pageCanvas.toDataURL('image/png');
        
        // Use consistent width calculation for all pages
        const imgWidth = pageWidth;
        const imgHeight = (pageCanvas.height * imgWidth) / pageCanvas.width;
        
        // Add image with consistent sizing - no additional scaling
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      }
    } else {
      // Handle single-page PDF (original logic with consistent sizing)
      const canvas = await generateSinglePageCanvas(divInvoice, invoice, documentType);
      const imgData = canvas.toDataURL('image/png');
      
      // Use same width calculation as multi-page
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    }

    const safeNumber = invoice.invoiceNumber || 'export';
    const documentTitle = documentType === 'delivery' 
      ? `Испратница #${safeNumber}` 
      : `Фактура #${safeNumber}`;
    
    // Create a clean filename for file system (no # character)
    const cleanFilename = documentType === 'delivery' 
      ? `Испратница-${safeNumber}_${new Date().toISOString()}.pdf`
      : `Фактура-${safeNumber}_${new Date().toISOString()}.pdf`;
    
    pdf.setProperties({ title: documentTitle });
    pdf.save(cleanFilename);

    const pdfBlob = pdf.output('blob');
    
    // Upload to server if requested
    if (uploadToServer) {
      try {
        showAlert('info', t('Прикачување PDF'), t('PDF документот се зачувува на серверот...'));
        const documentSubType = documentType === 'delivery' ? 'dnote' : 'invoices';
        const uploadResult = await uploadPdf(pdfBlob, cleanFilename, documentId, documentSubType);
        console.log('PDF uploaded successfully:', uploadResult);
        
        // Update the database with PDF filename based on document type
        if (documentId && uploadResult.file && uploadResult.file.filename) {
          try {
            const token = localStorage.getItem('token');
            if (token) {
              const endpointPath = documentType === 'delivery' 
                ? `dnote/${documentId}/pdf-filename`
                : `invoice/${documentId}/pdf-filename`;
              
              await fetch(`${apiUrl}/${endpointPath}`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ 
                  pdf_filename: uploadResult.file.filename,
                  original_filename: uploadResult.file.originalName,
                  file_size: uploadResult.file.size
                }),
              });
            }
          } catch (dbError) {
            console.error('Failed to update PDF filename in database:', dbError);
          }
        }
      } catch (uploadError) {
        console.error('Failed to upload PDF:', uploadError);
        showAlert('warning', t('Предупредување при прикачување'), t('PDF документот е успешно генериран, но не успеа прикачувањето на серверот: {error}', { error: uploadError.message }));
      }
    }

    const pdfUrl = URL.createObjectURL(pdfBlob);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <title>${documentTitle}</title>
            <meta charset="utf-8" />
            <style>html, body {height:100%;margin:0;} iframe {border:none;width:100%;height:100%;}</style>
          </head>
          <body>
            <iframe src="${pdfUrl}"></iframe>
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 15000);
    }

    closeAlert();
    // showAlert('success', 'PDF Generated', `PDF saved as ${cleanFilename} and opened for printing.`);

  } catch (error) {
    console.error('Error generating PDF:', error);
    closeAlert();
    showAlert('error', t('Неуспешно генерирање PDF'), t('Не може да се генерира PDF. Ве молиме обидете се повторно.'), error.message);
  }
};